// AGC: LotNotesCard — Collapsible card showing all notes for a lot
// AGC: Placed between Localização do Talhão and Auditoria on LotPage

import React, { useState, useEffect, useCallback } from 'react'
import { StickyNote, ChevronDown, ChevronUp, Camera, Video, Mic, FileText, MapPin, Clock, Loader2 } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { getNotesByLot } from '../../services/lotNotesService'
import { openInGoogleMaps } from '../../utils/gpsUtils'
import type { LotNote } from '../../types/lotNotes'

interface LotNotesCardProps {
  lotId: string
  onNoteClick?: (note: LotNote) => void
  // AGC: Increment to trigger re-fetch after saving a new note
  refreshTrigger?: number
}

const NOTE_ICONS: Record<string, React.FC<{ className?: string }>> = {
  photo: Camera,
  video: Video,
  audio: Mic,
  text: FileText,
}

const NOTE_COLORS: Record<string, string> = {
  photo: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
  video: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20',
  audio: 'text-red-500 bg-red-50 dark:bg-red-900/20',
  text: 'text-green-500 bg-green-50 dark:bg-green-900/20',
}

export const LotNotesCard: React.FC<LotNotesCardProps> = ({ lotId, onNoteClick, refreshTrigger = 0 }) => {
  const { t } = useTranslation()
  const [notes, setNotes] = useState<LotNote[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isExpanded, setIsExpanded] = useState(false)

  const loadNotes = useCallback(async () => {
    try {
      setIsLoading(true)
      const result = await getNotesByLot(lotId)
      setNotes(result)
    } catch (err) {
      console.error('AGC: Failed to load notes for card', err)
    } finally {
      setIsLoading(false)
    }
  }, [lotId])

  // AGC: Re-fetch when lotId changes or refreshTrigger increments (after saving a note)
  useEffect(() => {
    loadNotes()
  }, [lotId, refreshTrigger])

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
      ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
      {/* AGC: Header — always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
          <StickyNote className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t('notes.lotNotes')}
            </h3>
            {!isLoading && notes.length > 0 && (
              <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-bold rounded-full">
                {notes.length}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {t('notes.lotNotesSubtitle')}
          </p>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>

      {/* AGC: Expanded list */}
      {isExpanded && (
        <div className="border-t border-gray-100 dark:border-slate-700">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-8 px-4">
              <StickyNote className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('notes.noNotesYet')}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('notes.noNotesHint')}</p>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto divide-y divide-gray-100 dark:divide-slate-700">
              {notes.map(note => {
                const Icon = NOTE_ICONS[note.type] || FileText
                const colorClass = NOTE_COLORS[note.type] || NOTE_COLORS.text

                return (
                  <button
                    key={note.id}
                    onClick={() => onNoteClick?.(note)}
                    className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors"
                  >
                    {/* AGC: Type icon */}
                    <div className={`p-1.5 rounded-lg shrink-0 ${colorClass}`}>
                      <Icon className="w-4 h-4" />
                    </div>

                    {/* AGC: Content */}
                    <div className="flex-1 min-w-0">
                      {/* AGC: Thumbnail for photo/video */}
                      {note.mediaUrl && (note.type === 'photo') && (
                        <img
                          src={note.mediaThumbnailUrl || note.mediaUrl}
                          alt=""
                          className="w-full h-20 rounded-lg object-cover mb-1.5"
                        />
                      )}

                      {/* AGC: Text content or fallback name */}
                      {note.content ? (
                        <p className="text-sm text-gray-800 dark:text-gray-200 line-clamp-2">
                          {note.content}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {note.type === 'photo' ? t('notes.photoNote', 'Foto')
                            : note.type === 'video' ? t('notes.videoNote', 'Vídeo')
                            : note.type === 'audio' ? t('notes.audioNote', 'Áudio')
                            : t('notes.genericNote', 'Nota')}
                        </p>
                      )}

                      {/* AGC: Duration for audio/video — guard against Infinity/NaN */}
                      {note.mediaDuration != null && isFinite(note.mediaDuration) && note.mediaDuration > 0 && (
                        <p className="text-xs text-gray-500">
                          {Math.floor(note.mediaDuration / 60)}:{Math.floor(note.mediaDuration % 60).toString().padStart(2, '0')}
                        </p>
                      )}

                      {/* AGC: Meta row */}
                      <div className="flex items-center gap-3 mt-1">
                        <div className="flex items-center gap-1 text-[10px] text-gray-400">
                          <Clock className="w-3 h-3" />
                          {formatDate(note.createdAt)}
                        </div>
                        {note.gpsLocation && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              openInGoogleMaps(note.gpsLocation!.latitude, note.gpsLocation!.longitude)
                            }}
                            className="flex items-center gap-0.5 text-[10px] text-green-500 hover:underline"
                          >
                            <MapPin className="w-3 h-3" />
                            GPS
                          </button>
                        )}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default LotNotesCard
